import { RefObject, useLayoutEffect, useRef } from 'react';

import {
  placeScrollAnchor,
  takeScrollAnchor,
  type ScrollAnchor,
  type ScrollAnchorItem,
} from '../lib';

// The template editor marks headings and lines with `data-section-title` and
// `data-line-id`, a note and its fallback with `data-note-*`.
const ANCHOR_SELECTOR =
  '[data-section-title], [data-line-id], [data-note-section-head], [data-note-line-id]';
const TOOLBAR_SELECTOR = '[data-component="workspace-document-toolbar"]';
const SCROLLING_OVERFLOWS = ['auto', 'scroll', 'overlay'];
const USER_MOVES = ['wheel', 'touchstart', 'pointerdown', 'keydown'];

const readAnchorKey = (element: HTMLElement) => {
  const { sectionTitle, lineId, noteLineId } = element.dataset;
  const line = lineId || noteLineId;
  const section =
    sectionTitle ||
    (element.hasAttribute('data-note-section-head')
      ? element.closest<HTMLElement>('[data-note-section-id]')?.dataset
          .noteSectionId
      : undefined);
  if (line) {
    return `line:${line}`;
  }
  return section ? `section:${section}` : undefined;
};

const getPageScroller = () =>
  (document.scrollingElement ?? document.documentElement) as HTMLElement;

const findScrollContainer = (element: HTMLElement | null): HTMLElement => {
  if (!element) {
    return getPageScroller();
  }
  return SCROLLING_OVERFLOWS.includes(getComputedStyle(element).overflowY)
    ? element
    : findScrollContainer(element.parentElement);
};

const getContainerTop = (container: HTMLElement) =>
  container === getPageScroller() ? 0 : container.getBoundingClientRect().top;

// The first element of a key wins: a line's floating tools repeat its id.
const readAnchorItems = (root: HTMLElement, containerTop: number) =>
  Array.from(root.querySelectorAll<HTMLElement>(ANCHOR_SELECTOR)).reduce<
    ScrollAnchorItem[]
  >((items, element) => {
    const key = readAnchorKey(element);
    if (!key || items.some((item) => item.key === key)) {
      return items;
    }
    const { top, bottom } = element.getBoundingClientRect();
    return [
      ...items,
      { key, top: top - containerTop, bottom: bottom - containerTop },
    ];
  }, []);

// Keeps the document at the place it is read while `view` switches what
// `root` holds between two renderings of it: the caller keeps the place
// right before the switch, and it is restored once the other view rendered.
export const useKeepScrollPlace = (
  view: unknown,
  root: RefObject<HTMLElement>,
) => {
  const kept = useRef<{ container: HTMLElement; anchor: ScrollAnchor }>();

  useLayoutEffect(() => {
    const place = kept.current;
    kept.current = undefined;
    const element = root.current;
    if (!place || !element) {
      return undefined;
    }
    const { container, anchor } = place;
    const restore = () => {
      container.scrollTop = placeScrollAnchor({
        anchor,
        items: readAnchorItems(element, getContainerTop(container)),
        scroll: container,
      });
    };
    restore();
    // A view just mounted grows as its fields render, a task or more after
    // its first frame: the place follows its size until the user moves on.
    const follow = new ResizeObserver(restore);
    const stop = () => {
      follow.disconnect();
      USER_MOVES.forEach((type) =>
        window.removeEventListener(type, stop, { capture: true }),
      );
    };
    Array.from(element.children).forEach((child) => follow.observe(child));
    USER_MOVES.forEach((type) =>
      window.addEventListener(type, stop, { capture: true, passive: true }),
    );
    return stop;
    // The place is restored once per switch of the view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  return () => {
    const element = root.current;
    if (!element) {
      return;
    }
    const container = findScrollContainer(element.parentElement);
    const containerTop = getContainerTop(container);
    const toolbar = element.querySelector(TOOLBAR_SELECTOR);
    kept.current = {
      container,
      anchor: takeScrollAnchor({
        items: readAnchorItems(element, containerTop),
        scroll: container,
        readingTop: Math.max(
          0,
          (toolbar?.getBoundingClientRect().bottom ?? containerTop) -
            containerTop,
        ),
      }),
    };
  };
};
